import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react'
import { useEffect } from 'react';
import { useCallback } from 'react';
import { useState } from 'react'
import { Alert, Badge, Breadcrumb, Button, Card, Col, Container, Modal, Row, Table } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ModelDeploySettings, ModelDeploymentStatus, Models } from '../../api/Models';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import AppLoader from '../../components/common/AppLoader';
import AssetRibbon from '../../components/common/AssetRibbon';
import { formatDate, setObjectType } from '../../utils';
import FileSaver from 'file-saver';
import ScriptEditor from '../../components/common/ScriptEditor';
import { useInterval } from '../../utils/useInterval';
import { TailSpin } from 'react-loader-spinner';
import { Form, Input, Tooltip } from 'antd';
import { BdaModel } from '../../models/BdaModel';
import { DatabaseDatasource, ObjectStorageDatasource } from '../../models/Datasource';
import { DataRetriever } from '../../api/DataRetriever';
import { DataspaceConnector } from '../../models/DataSpace';
import DataspaceConnectorItem from '../../components/list-items/DataspaceConnectorItem';
import AssetProjectsEditor from '../../components/forms/AssetProjectsEditor';

const formLayout = {
  labelCol: {
    xs: 24,
    md: 5,
  },
  wrapperCol: {
    xs: 24,
    md: 18,
  },
}


export default function ModelDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [model, setModel] = useState<BdaModel>();
  const [error, setError] = useState(false);
  const [modelDeploymentStatus, setModelDeploymentStatus] = useState<ModelDeploymentStatus>();
  const [modelMetadata, setModelMetadata] = useState<Record<string, any>>();

  const [forcePolling, setForcePolling] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [connectors, setConnectors] = useState<DataspaceConnector[]>();
  const [form] = Form.useForm();
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const [showProjectsModal, setShowProjectsModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const isSeldonModel = useMemo(() => model?.created && model.format !== null, [model]);
  const predictionLink = useMemo(() => window.__env.MODEL_INFER_URL.replace("$MODEL_ID", id!), [id]);

  const initConnectors = useCallback(() => {
    const alidaUrl = window.location.href;

    DataRetriever.getDataspaceConnectorsByUrl(alidaUrl)
      .then(
        ({ data }) => setConnectors(data),
        (err) => {
          setConnectors([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('connectors-error', message, `Model Connectors Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  const initModel = useCallback(() => {
    Models.getModel(+id!)
      .then(
        ({ data: model }) => {
          setModel(model);
          initConnectors();
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('model-error', message, `Model Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, initConnectors, showToast]);

  useEffect(() => {
    if (model && isSeldonModel && modelDeploymentStatus?.ready && !modelMetadata) {
      Models.getModelMetadata(+id!)
        .then(
          ({ data }) => setModelMetadata(data),
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast('model-metadata-error', message, `Model Metadata Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }
  }, [id, showToast, model, isSeldonModel, modelDeploymentStatus, modelMetadata]);

  const getModelStatus = useCallback(() => {
    Models.getModelStatus(+id!)
      .then(
        () => setModelDeploymentStatus({ label: 'Deployed', ready: true }),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          if (status === 404) setModelDeploymentStatus({ ready: false, label: "Not Deployed" })
          else showToast('model-status-error', message, `Model Status Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast])

  useEffect(() => initModel(), [initModel]);

  useInterval(() => getModelStatus(), forcePolling ? 10000 : undefined);

  useEffect(() => {
    if (model && isSeldonModel) getModelStatus();
    if (isSeldonModel) form.setFieldsValue({ replicasNumber: 1, cpuLimit: 200, memoryLimit: 256 });
  }, [isSeldonModel, model, getModelStatus, form]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(predictionLink);
    showToast('text-copied', 'URL copied to clipboard!');
  }, [predictionLink, showToast]);

  const onDeleteModel = useCallback(() => setShowDeleteModal(true), []);
  const handleDeleteClose = useCallback(() => setShowDeleteModal(false), []);
  const handleSettingsClose = useCallback(() => setShowSettingsModal(false), []);

  const deleteModel = () => {
    showLoader();

    Models.deleteModel(model!.id)
      .then(
        () => {
          showToast('model-delete-success', 'Model deleted successfully.', 'Success', 'success');
          hideLoader();
          handleDeleteClose();
          navigate('/models');
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('model-error', message, `Model Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const startModelDeployment = (settings: ModelDeploySettings) => {
    showLoader();

    const body = { ...settings };
    body.cpuLimit += "m";
    body.memoryLimit += "Mi";

    Models.deployModel(+id!, body)
      .then(
        () => {
          hideLoader();
          setForcePolling(true);
          setShowSettingsModal(false);
          showToast('model-start-success', 'Model deployment started successfully', 'Model Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('model-error', message, `Model Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const stopModelDeployment = () => {
    showLoader();

    Models.stopModel(+id!)
      .then(
        () => {
          hideLoader();
          setForcePolling(false);
          showToast('model-stop-success', 'Model deployment stopped successfully', 'Model Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('model-error', message, `Model Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const downloadModel = () => {
    showLoader();

    Models.downloadModel(model!.id)
      .then(
        ({ data: modelToDownload }) => {
          hideLoader();
          const blob = new Blob([modelToDownload], { type: 'application/zip' });
          FileSaver.saveAs(blob, `${model!.name}.zip`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('models-error', message, `Model Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handleToggleClick = () => {
    if (modelDeploymentStatus!.ready) stopModelDeployment();
    else setShowSettingsModal(true);
  }

  const handlePublishModelClick = useCallback(() => {
    navigate('publish');
  }, [navigate]);

  const openProjectsModal = () => setShowProjectsModal(true);
  const handleProjectsModalClose = () => setShowProjectsModal(false);

  const renderAsyncContent = () => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured. Please, try again later.</span>
      </Alert>
    )

    if (!model) return (
      <div className="d-flex justify-content-center py-5">
        <AppLoader size={300} />
      </div>
    )

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/models' }}>Models</Breadcrumb.Item>
          <Breadcrumb.Item active>{model.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Card className="item-detail-group main bg-model">
          <div className="item-ribbons">
            {(model.createdByServiceId === null) && (
              <AssetRibbon
                icon={solid('user')}
                color="info"
                tooltip="This has been created by a user."
              />
            )}
            {(model.createdByServiceId !== null) && (
              <AssetRibbon
                icon={solid('cogs')}
                color="dark"
                tooltip="This is an output from an execution."
              />
            )}
            {!model.created && (
              <AssetRibbon
                icon={solid('lock')}
                color="warning"
                tooltip="This has not been created yet."
              />
            )}
          </div>

          <FontAwesomeIcon className="item-icon" icon={solid('graduation-cap')} />

          <Row className="gx-0 justify-content-end">
            <Badge className="w-auto shadow">{model.accessLevel}</Badge>
          </Row>

          <div className="p-2 py-4">
            <Row className="gx-0 align-items-center">
              <Col className="pe-3">
                <h3 className="mb-0">{model.name}</h3>
                <p className="text-trailing mb-3">{model.description}</p>
              </Col>

              <div className="w-auto">
                <Tooltip title="Manage Projects">
                  <button className="circle-button p-4 me-3" onClick={openProjectsModal}>
                    <span className="fa-layers">
                      <FontAwesomeIcon icon={solid('folder')} transform="left-14 grow-2" />
                      <FontAwesomeIcon icon={solid('cog')} transform="shrink-4 down-8 right-3" />
                    </span>
                  </button>
                </Tooltip>
                {isSeldonModel && modelDeploymentStatus && (
                  <Tooltip title="Deploy Model">
                    <button className="circle-button p-4 me-3" onClick={handleToggleClick}>
                      <FontAwesomeIcon icon={modelDeploymentStatus.ready ? solid('pause') : solid('play')} />
                    </button>
                  </Tooltip>
                )}
                <Tooltip title="Publish">
                  <button className="circle-button p-4" onClick={handlePublishModelClick}>
                    <FontAwesomeIcon icon={solid('share-from-square')} />
                  </button>
                </Tooltip>
                <Tooltip title="Download">
                  <button className="circle-button p-4 ms-3" onClick={downloadModel}>
                    <FontAwesomeIcon icon={solid('download')} />
                  </button>
                </Tooltip>
                <Tooltip title="Delete">
                  <button className="circle-button p-4 ms-3" onClick={onDeleteModel}>
                    <FontAwesomeIcon icon={solid('trash')} />
                  </button>
                </Tooltip>
              </div>
            </Row>
          </div>

          <Row className="gx-0 justify-content-between">
            <Col xs="auto">
              <div className="framework-item bg-highlight">
                <div className="framework-image" style={{ backgroundImage: `url(${model.framework.imageUrl})` }}></div>
                <div className="ps-3">
                  <h6 className="mb-0 d-inline me-2">{model.framework.name}</h6>
                  <small className="text-trailing">{model.framework.version}</small>
                </div>
              </div>
            </Col>

            <div className="d-flex flex-wrap">
              {
                model.tags.map((tag, index) => <Badge className="m-1" pill={true} key={index}>{tag}</Badge>)
              }
            </div>
          </Row>

          <div className="item-dates">
            <div className="d-inline-block">
              <b>Created:</b>
              <span className="ms-1">{formatDate(model.createDateTime)}</span>
            </div>
            <div className="d-inline-block ms-2">
              <b>Last Update:</b>
              <span className="ms-1">{formatDate(model.updateDateTime)}</span>
            </div>
          </div>
        </Card>

        {isSeldonModel && (
          <Card className="item-detail-group mb-3">
            <div className="item-detail-highlight">
              <FontAwesomeIcon icon={solid('cube')} fixedWidth />
              <h5 className="d-inline ms-2 mb-0">Model Deployment</h5>
            </div>

            <Card.Body>
              <Table className="mb-0">
                <thead>
                  <tr>
                    <th className="border-0" style={{ width: 200 }}></th>
                    <th className="border-0"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="col">Status</th>
                    <td className="text-end">
                      {modelDeploymentStatus
                        ? (
                          <Badge bg={modelDeploymentStatus.ready ? "success" : "warning"}>
                            <FontAwesomeIcon icon={modelDeploymentStatus.ready ? solid('cog') : solid('exclamation-triangle')} spin={modelDeploymentStatus.ready} />
                            <span className="ms-1">{modelDeploymentStatus.label}</span>
                          </Badge>
                        )
                        : (
                          <div className="d-flex justify-content-end">
                            <TailSpin width={20} height={20} color="#677ab1" />
                          </div>
                        )}
                    </td>
                  </tr>
                  {modelDeploymentStatus?.ready && modelMetadata && (
                    <>
                      <tr>
                        <th scope="col">Prediction</th>
                        <td className="text-end">
                          <Badge bg="light" className="border action text-muted" onClick={copyToClipboard}>
                            <FontAwesomeIcon icon={solid('clipboard')} />
                            <span className="ms-2">{predictionLink}</span>
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th scope="col">Model Metadata</th>
                        <td className="text-end">
                          <ScriptEditor value={modelMetadata} height={300} readOnly />
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {model.datasource
          ? (
            <Card className="item-detail-group mb-3">
              <div className="item-detail-highlight">
                <FontAwesomeIcon icon={solid('database')} fixedWidth />
                <h5 className="d-inline ms-2 mb-0">Model Source</h5>
              </div>

              <Card.Body>
                <Table className="mb-0">
                  <tbody>
                    <tr>
                      <th scope="col">Name</th>
                      <td className="text-end">
                        <Link to={`/datasources/${model.datasource.id}`} target="_blank" rel="noopener noreferrer">{model.datasource.name}</Link>
                      </td>
                    </tr>
                    <tr>
                      <th scope="col">Description</th>
                      <td className="text-end">{model.datasource.description}</td>
                    </tr>
                    <tr>
                      <th scope="col">Driver</th>
                      <td className="text-end">{model.datasource.driverType}</td>
                    </tr>
                    <tr>
                      <th scope="col">Host</th>
                      <td className="text-end">{model.datasource.host}</td>
                    </tr>
                    <tr>
                      <th scope="col">Port</th>
                      <td className="text-end">{model.datasource.port}</td>
                    </tr>
                    {model.datasource.type === "objectStorage" && setObjectType<ObjectStorageDatasource>(model.datasource) && (
                      <tr>
                        <th scope="col">Bucket</th>
                        <td className="text-end">
                          <FontAwesomeIcon icon={solid('bucket')} size="sm" />
                          <span className="ms-2">{model.datasource.bucket}</span>
                        </td>
                      </tr>
                    )}
                    {model.datasource.type === "database" && setObjectType<DatabaseDatasource>(model.datasource) && (
                      <tr>
                        <th scope="col">Database</th>
                        <td className="text-end">
                          <FontAwesomeIcon icon={solid('database')} size="sm" />
                          <span className="ms-2">{model.datasource.database}</span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )
          : (
            <Alert variant="warning" className="py-2 mb-3">
              <FontAwesomeIcon icon={solid('exclamation-triangle')} />
              <span className="ms-2">Model data source not found.</span>
            </Alert>
          )
        }

        <Card className="item-detail-group mb-3">
          <div className="item-detail-highlight">
            <FontAwesomeIcon icon={solid('info-circle')} fixedWidth />
            <h5 className="d-inline ms-2 mb-0">Model Info</h5>
          </div>

          <Card.Body>
            <Table>
              <thead>
                <tr>
                  <th className="border-0" style={{ width: 200 }}></th>
                  <th className="border-0"></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="col">Location</th>
                  <td className="text-end">{model.location}</td>
                </tr>
                <tr>
                  <th scope="col">Format</th>
                  <td className="text-end">{model.format || 'N/A'}</td>
                </tr>
                {model.createdByBdaId && (
                  <tr>
                    <th scope="col">Created By Application</th>
                    <td className="text-end">
                      <Link className="d-inline-block" to={`/bda-applications/${model.createdByBdaId}`} target="_blank" rel="noopener noreferer">
                        <Badge bg="light" className="border text-primary">
                          <FontAwesomeIcon icon={solid('laptop-code')} />
                          <span className="ms-2">{model.createdByBdaId}</span>
                        </Badge>
                      </Link>
                    </td>
                  </tr>
                )}
                {model.createdByServiceId && (
                  <tr>
                    <th scope="col">Algorithm</th>
                    <td className="text-end">
                      <Link className="d-inline-block" to={`/services/${model.createdByServiceId}`} target="_blank" rel="noopener noreferer">
                        <Badge bg="light" className="border text-primary">
                          <FontAwesomeIcon icon={solid('cubes')} />
                          <span className="ms-2">{model.algorithmName}</span>
                        </Badge>
                      </Link>
                    </td>
                  </tr>
                )}
                {model.trainingDataset && (
                  <tr>
                    <th scope="col">Training Dataset</th>
                    <td className="text-end">
                      <Link className="d-inline-block" to={`/datasets/${model.trainingDataset.id}`} target="_blank" rel="noopener noreferer">
                        <Badge bg="light" className="border text-primary">
                          <FontAwesomeIcon icon={solid('database')} />
                          <span className="ms-2">{model.trainingDataset.name}</span>
                        </Badge>
                      </Link>
                    </td>
                  </tr>
                )}
                {model.inputs && model.inputs !== "" && (
                  <tr>
                    <th scope="col">Input Columns</th>
                    <td className="text-end">
                      <div className="max-h-md">
                        {model.inputs.split(",").map(col => <Badge key={col} bg="light" className="border text-secondary me-1">{col}</Badge>)}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {model.properties && (
              <>
                <h6>Properties</h6>
                <Table className="mb-0">
                  <tbody>
                    {model.properties.map((wrapper, index) => {
                      const [key] = Object.keys(wrapper);
                      const value = wrapper[key];

                      if (typeof value === "string" && value.includes("{")) {
                        try {
                          JSON.parse(value);

                          return (
                            <tr key={index}>
                              <th scope="col">{key}</th>
                              <td>
                                <ScriptEditor value={value} readOnly height={150} />
                              </td>
                            </tr>
                          )
                        }
                        catch (err) { }
                      }

                      return (
                        <tr key={index}>
                          <th scope="col">{key}</th>
                          <td className="text-end"><code>{value}</code></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </>
            )}
          </Card.Body>
        </Card>

        <Card className="item-detail-group">
          <div className="item-detail-highlight">
            <FontAwesomeIcon icon={solid('store')} fixedWidth />
            <h5 className="d-inline ms-2 mb-0">Available at</h5>
          </div>

          <Card.Body className="p-0 pt-1 px-2">
            {!connectors && (
              <div className="d-flex justify-content-center py-3">
                <TailSpin width={60} height={60} color="#677ab1" />
              </div>
            )}

            {connectors?.length === 0
              ? (
                <Alert variant="info" className="mt-2 p-2">
                  <FontAwesomeIcon icon={solid('info-circle')} />
                  <span className="ms-2">No offers found for this model.</span>
                </Alert>
              )
              : (
                <Row>
                  {connectors?.map((connector) => (
                    <Col key={connector.id} className="px-2 mb-3" md={6} lg={4}>
                      <DataspaceConnectorItem className="h-100" connector={connector} actions={<></>} />
                    </Col>
                  ))}
                </Row>
              )
            }
          </Card.Body>
        </Card>
      </>
    )
  }

  return (
    <Container className="mt-3">
      <Helmet>
        <title>Model {id} Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <br />

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Model</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete model <b>{model && model.name}</b>?</p>

          <Alert className="p-2" variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteModel}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSettingsModal} onHide={handleSettingsClose}>
        <Modal.Header closeButton>
          <Modal.Title>Model Deployment Settings</Modal.Title>
        </Modal.Header>
        <Form form={form} {...formLayout} onFinish={startModelDeployment}>
          <Modal.Body>
            <Form.Item
              label="Replicas"
              name="replicasNumber"
              extra="Defaults to 1"
              rules={[
                { min: 1, type: 'number', message: 'Replicas must be greater than or equal to 1' },
                { required: true, message: 'Replicas is required' },
              ]}>
              <Input placeholder="Number of replicas" type="number" min={1} />
            </Form.Item>
            <Form.Item
              label="CPU Limit"
              name="cpuLimit"
              extra="Defaults to 200m"
              rules={[{ required: true, message: 'CPU Limit is required' }]}
            >
              <Input placeholder="Limit on the CPU" type="number" suffix="m" />
            </Form.Item>
            <Form.Item
              label="Memory Limit"
              name="memoryLimit"
              extra="Defaults to 256Mi"
              rules={[{ required: true, message: 'Memory Limit is required' }]}
            >
              <Input placeholder="Limit on RAM Memory" type="number" suffix="Mi" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button variant="light" size="sm" className="border" type="button" onClick={handleSettingsClose}>Close</Button>
            <Button variant="success" size="sm" type="submit">
              <FontAwesomeIcon icon={regular('play-circle')} />
              <span className="ms-2">Start</span>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showProjectsModal} onHide={handleProjectsModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Model Projects</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AssetProjectsEditor assetId={+id!} assetType="model" assetLabel="Model" />
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="light" className="border" onClick={handleProjectsModalClose} size="sm">Cancel</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
